import React, { useRef, useState, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import styled from 'styled-components'
import Node from './Node'
import Edge from './Edge'

const VisualizationContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 1;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(
        circle at 30% 30%,
        rgba(64, 224, 208, 0.07) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 70% 70%,
        rgba(43, 158, 146, 0.07) 0%,
        transparent 50%
      );
    pointer-events: none;
  }

  canvas {
    pointer-events: auto;
  }
`

const generateNodes = () => {
  const nodes = []
  
  // Helper function to create nodes in a brain hemisphere shape
  const createHemisphereNodes = (count, side, color, type, yOffset = 0) => {
    const radius = 8
    const heightScale = 0.6
    
    for (let i = 0; i < count; i++) {
      // Create a more organic distribution
      const phi = Math.acos(-1 + (2 * i) / count)
      const theta = Math.sqrt(count * Math.PI) * phi
      
      // Calculate position with adjusted height and radius
      const x = side * (radius * Math.cos(theta) * Math.sin(phi))
      const y = heightScale * radius * Math.sin(theta) * Math.sin(phi) + yOffset
      const z = radius * Math.cos(phi)
      
      // Add random offset for more natural look
      const jitter = 0.8
      const position = [
        x + (Math.random() - 0.5) * jitter,
        y + (Math.random() - 0.5) * jitter,
        z + (Math.random() - 0.5) * jitter
      ]
      
      nodes.push({
        id: `${type}-${i}`,
        type,
        position,
        color,
        task: getRandomTask(type),
        status: Math.random() > 0.3 ? 'Active' : 'Standby'
      })
    }
  }

  // Create nodes in left and right hemispheres
  // Left hemisphere (lighter turquoise)
  createHemisphereNodes(10, -1, '#40E0D0', 'coder', 1)
  
  // Right hemisphere (darker turquoise)
  createHemisphereNodes(5, 1, '#2B9E92', 'artist', -1)
  
  // Distributed nodes (mix of both colors)
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2
    const radius = 6 + Math.random() * 2
    const height = (Math.random() - 0.5) * 4
    
    nodes.push({
      id: `social-${i}`,
      type: 'social',
      position: [
        Math.cos(angle) * radius,
        height,
        Math.sin(angle) * radius
      ],
      color: i % 2 === 0 ? '#40E0D0' : '#2B9E92',
      task: getRandomTask('social'),
      status: Math.random() > 0.3 ? 'Active' : 'Monitoring'
    })
  }

  // Central Hub (brighter turquoise)
  nodes.push({
    id: 'user',
    type: 'user',
    position: [0, 0, 0],
    color: '#5FFBEB',
    task: 'Neural Network Coordination',
    status: 'Online'
  })

  return nodes
}

const getRandomTask = (type) => {
  const tasks = {
    coder: [
      'Processing algorithms',
      'Optimizing neural pathways',
      'Analyzing data patterns',
      'Developing new connections',
      'Synchronizing nodes'
    ],
    artist: [
      'Generating creative patterns',
      'Synthesizing visual data',
      'Processing sensory input',
      'Creating neural art',
      'Mapping emotional responses'
    ],
    social: [
      'Coordinating node clusters',
      'Facilitating neural communication',
      'Monitoring network activity',
      'Balancing synaptic loads',
      'Optimizing signal strength'
    ]
  }
  return tasks[type][Math.floor(Math.random() * tasks[type].length)]
}

const generateEdges = (nodes) => {
  const edges = []
  const userNode = nodes.find(n => n.id === 'user')

  // Create synaptic connections
  nodes.forEach((node, i) => {
    if (node.id !== 'user') {
      // Connect to central hub with varying intensity
      edges.push({
        id: `${node.id}-user`,
        start: node.position,
        end: userNode.position,
        color: node.color,
        intensity: Math.random() * 0.5 + 0.5
      })

      // Connect to nearby nodes in the same hemisphere
      const nearbyNodes = nodes
        .filter(n => n.id !== 'user' && n.id !== node.id)
        .sort((a, b) => {
          const distA = distance(node.position, a.position)
          const distB = distance(node.position, b.position)
          return distA - distB
        })
        .slice(0, 2) // Reduced connections for cleaner look

      nearbyNodes.forEach(target => {
        edges.push({
          id: `${node.id}-${target.id}`,
          start: node.position,
          end: target.position,
          color: node.color,
          intensity: Math.random() * 0.3 + 0.2
        })
      })
    }
  })

  return edges
}

const distance = (pos1, pos2) => {
  return Math.sqrt(
    Math.pow(pos1[0] - pos2[0], 2) +
    Math.pow(pos1[1] - pos2[1], 2) +
    Math.pow(pos1[2] - pos2[2], 2)
  )
}

function NetworkVisualization() {
  const [selectedNode, setSelectedNode] = useState(null)
  const controlsRef = useRef()

  const nodes = useMemo(() => generateNodes(), [])
  const edges = useMemo(() => generateEdges(nodes), [nodes])

  return (
    <VisualizationContainer>
      <Canvas
        camera={{ position: [15, 10, 20], fov: 45 }}
        dpr={window.devicePixelRatio}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'transparent'
        }}
      >
        {/* Ambient light for base illumination */}
        <ambientLight intensity={0.2} />
        
        {/* Main light source */}
        <pointLight position={[10, 10, 10]} intensity={0.8} color="#40E0D0" />
        
        {/* Secondary lights for depth */}
        <pointLight position={[-20, -10, -10]} intensity={0.5} color="#2B9E92" />
        <pointLight position={[20, -10, -10]} intensity={0.5} color="#5FFBEB" />
        
        {/* Edges (synapses) */}
        {edges.map((edge) => (
          <Edge key={edge.id} {...edge} />
        ))}

        {/* Nodes (neurons) */}
        {nodes.map((node) => (
          <Node
            key={node.id}
            {...node}
            onClick={() => setSelectedNode(node)}
          />
        ))}

        <OrbitControls
          ref={controlsRef}
          enablePan={false}
          enableZoom={true}
          minDistance={15}
          maxDistance={40}
          autoRotate
          autoRotateSpeed={0.3}
          maxPolarAngle={Math.PI * 0.65}
          minPolarAngle={Math.PI * 0.25}
          dampingFactor={0.05}
          rotateSpeed={0.5}
          zoomSpeed={0.8}
        />
      </Canvas>
    </VisualizationContainer>
  )
}

export default NetworkVisualization
